import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MovingBorderWrapper } from "@/components/ui/moving-border";
import { SEO, seoConfig } from "@/hooks/useSEO";

// Images
import heroImage from "@/assets/usecases/local/hero.webp";
import feature1Image from "@/assets/usecases/local/feature-1.webp";
import feature2Image from "@/assets/usecases/local/feature-2.webp";
import feature3Image from "@/assets/usecases/local/feature-3.webp";

const features = [
  {
    number: "1",
    title: "Lancez une vente flash ultra-efficace",
    text: "Du stock en trop ? Une soirée calme ? Ne croisez plus les doigts. Créez l'affluence. Envoyez \"Happy Hour jusqu'à 19h !\" ou \"-50% sur tout jusqu'à 17h\" et regardez vos clients débarquer. Annoncez sur vos réseaux que l'offre est réservée aux détenteurs de la carte pour inciter à l'installation.",
    image: feature1Image,
    imagePosition: "left" as const,
  },
  {
    number: "2",
    title: "L'alerte automatique qui attire les passants",
    text: "Configurez une zone géographique autour de votre commerce. Quand un client qui a votre carte entre dans cette zone, il reçoit automatiquement une notification. \"Vous passez dans le coin ? Café offert dès maintenant !\" Le passant devient client.",
    image: feature2Image,
    imagePosition: "right" as const,
  },
  {
    number: "3",
    title: "Comblez les créneaux de dernière minute",
    text: "Une annulation au salon, à la salle de sport, au cabinet ? C'est du chiffre perdu. Envoyez une notification instantanée à vos clients VIP avec une petite réduction pour qui peut prendre le créneau. Transformez une perte en journée complète.",
    image: feature3Image,
    imagePosition: "left" as const,
  },
];

const badges = [
  "Stock à écouler",
  "Soirée calme à remplir",
  "Créneau libre à combler",
];

export default function UseCasesLocal() {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO {...seoConfig.useCasesLocal} />
      <Header />

      {/* SECTION 1 - HERO */}
      <section className="relative pt-24 lg:pt-40 pb-[37.5px] sm:pb-[47.5px] lg:pb-[65px] overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12 xl:px-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center">
            {/* Left: Text content */}
            <div className="max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-foreground mb-6">
                Transformez un mardi calme <span className="text-[#0c97a9]">en journée record</span>
              </h1>

              <p className="leading-relaxed mb-8 font-medium text-[#4f5b5d] text-base sm:text-lg">
                Envoyez une offre spéciale à vos clients fidèles au moment où ils passent près de chez vous. <span className="font-semibold text-[#0c97a9]">Unotif</span> déclenche automatiquement des notifications géolocalisées qui transforment les passants en clients.
              </p>

              <div className="flex flex-col items-center lg:items-start">
                <MovingBorderWrapper duration={3000} borderRadius="0.5rem">
                  <Button asChild size="lg" className="bg-primary hover:bg-[hsl(187,87%,32%)] text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 text-base px-8 py-6">
                    <a href="https://app.unotif.com/auth?mode=signup">
                      Créez votre première carte
                    </a>
                  </Button>
                </MovingBorderWrapper>
                <p className="text-sm text-muted-foreground mt-2">
                  👉 Sans engagement, sans carte bancaire
                </p>
              </div>
            </div>

            {/* Right: Hero Image */}
            <div className="hidden lg:flex justify-center lg:justify-end items-center">
              <img
                src={heroImage}
                alt="Commerces locaux avec Unotif"
                loading="lazy"
                className="rounded-2xl max-w-full h-auto"
                style={{
                  boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 - Trois façons de ramener du monde à la demande */}
      <section className="bg-[#FAFBFB] pt-[37.5px] sm:pt-[47.5px] lg:pt-[65px] pb-[37.5px] sm:pb-[47.5px] lg:pb-[65px]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-6">
              Trois façons de ramener du monde <span className="text-[#0c97a9]">à la demande</span>
            </h2>
            <p className="text-base sm:text-lg font-medium leading-relaxed text-[#4f5b5d]">
              Votre carte Wallet est un lien direct avec vos meilleurs clients. Voici comment les faire venir.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-12 sm:space-y-16 lg:space-y-20">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                  feature.imagePosition === "right" ? "lg:grid-flow-dense" : ""
                }`}
              >
                {/* Image */}
                <div className={feature.imagePosition === "right" ? "lg:col-start-2" : ""}>
                  <img
                    src={feature.image}
                    alt={feature.title}
                    loading="lazy"
                    className="rounded-2xl w-full h-auto"
                    style={{
                      boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
                    }}
                  />
                </div>

                {/* Text */}
                <div className={feature.imagePosition === "right" ? "lg:col-start-1 lg:row-start-1" : ""}>
                  <div className="flex items-start gap-4">
                    <span className="text-4xl sm:text-5xl font-bold text-[#0c97a9]">
                      {feature.number}.
                    </span>
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4">
                        {feature.title}
                      </h3>
                      <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
                        {feature.text}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Result text */}
          <div className="text-center mt-12 sm:mt-16 lg:mt-20 max-w-3xl mx-auto">
            <p className="text-base sm:text-lg font-semibold leading-relaxed text-[#074155]">
              Résultat : vous remplissez votre boutique quand vous le décidez, en totale autonomie.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3 - Arrêtez de subir les journées creuses */}
      <section className="pt-[37.5px] sm:pt-[47.5px] lg:pt-[65px] pb-16 sm:pb-20 lg:pb-24">
        <div className="container mx-auto px-6 lg:px-12 xl:px-20">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-6">
              Arrêtez de subir les journées creuses.
            </h2>
            <p className="text-base sm:text-lg font-medium leading-relaxed text-[#4f5b5d] mb-4">
              Vous avez un lien direct avec vos meilleurs clients dans votre poche. Il est temps de l'utiliser. Arrêtez de dépenser dans des publicités qui ne marchent pas et commencez à envoyer des offres qui ramènent du monde immédiatement.
            </p>
            <p className="text-base sm:text-lg font-medium leading-relaxed text-[#4f5b5d] mb-10">
              Votre prochaine journée record est à une notification près.
            </p>

            {/* Badges */}
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-6">
              {badges.map((badge, index) => (
                <span
                  key={index}
                  className="rounded-full border border-[#074155] bg-white px-4 py-2 text-sm sm:text-base font-medium text-[#074155]"
                >
                  {badge}
                </span>
              ))}
            </div>

            {/* Callout */}
            <p className="text-base sm:text-lg font-medium text-[#074155]">
              👀 Remplissez votre boutique maintenant.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4 - CTA Final */}
      <section className="pb-16 sm:pb-20 lg:pb-24">
        <div className="container mx-auto px-6 lg:px-12 xl:px-20">
          <Card className="max-w-2xl mx-auto text-center p-8 sm:p-10 lg:p-12 border border-gray-200 shadow-lg">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#074155] mb-4">
              Votre canal de vente locale en 5 minutes !
            </h2>
            <p className="text-base sm:text-lg font-medium text-[#4f5b5d] mb-8">
              Testez Unotif gratuitement pour votre commerce.
            </p>
            <div className="flex flex-col items-center">
              <Button asChild size="lg" className="bg-primary hover:bg-[hsl(187,87%,32%)] text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 text-base px-8 py-6">
                <a href="https://app.unotif.com/auth?mode=signup">
                  Commencer maintenant
                </a>
              </Button>
              <p className="text-sm text-muted-foreground mt-3">
                Sans carte bancaire
              </p>
            </div>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
}
